import React, { useEffect } from 'react'
import * as yup from 'yup'
import Tippy from '@tippyjs/react'
import { useFormik } from 'formik'
import Footer from '@app/components/Footer'
import Navbar from '@app/components/Navbar'
import TextField from '@app/components/TextField'
import Button from '@app/components/Button/Button'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import { useNavigate } from 'react-router-dom'
import { QuizStep, updateQuiz } from '@app/store/slices/quiz'
import { FormattedMessage, useIntl } from 'react-intl'
import { usePostUserMutation } from '@app/store/api/customer'
import Spinner from '@app/components/Spinner'
import useCampaignHref from '@app/hooks/useCampaignHref'
import S from './styles'
import QuizHeader from './components/QuizHeader'
import { validate } from './constants'
import useQuizRedirect from './useQuizRedirect'
import RedirectPartialView from './RedirectPartialView'

const CustomerDetails = () => {
  const quiz = useAppSelector(state => state.quiz)
  const { messages } = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [post, res] = usePostUserMutation()

  const { isRedirecting } = useQuizRedirect({ requestedStep: QuizStep.CUSTOMER })
  const { href: quizModelHref } = useCampaignHref({ requestPath: 'quiz/model' })

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      name: quiz.customerName,
      email: quiz.customerEmail,
    },
    validationSchema: yup.object({
      name: yup
        .string()
        .isValidName(messages['static.form.error_name_format'] as string)
        .required(messages['quiz_customer_page.content.questions.customer_name.validation_message'] as string),
      email: yup
        .string()
        .matches(validate.email, messages['static.form.error_email_format'] as string)
        .required(messages['quiz_customer_page.content.questions.customer_email.validation_message'] as string),
    }),
    onSubmit: values => {
      post({ name: values.name, email: values.email })
    },
  })

  /**
   * Trims any leading or trailing white space from the value in memory
   */
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.trim()
    formik.handleChange(e)
  }

  /**
   * Update the store with the user's details if the request was successful
   */
  useEffect(() => {
    const currentRes = res
    if (currentRes.isSuccess) {
      dispatch(
        updateQuiz({
          customerName: currentRes.data.name,
          customerEmail: currentRes.data.email,
          customerId: currentRes.data.id,
          step: QuizStep.MODEL,
          prevStep: QuizStep.CUSTOMER,
        })
      )
    }
  }, [res, dispatch])

  /**
   * Redirect to the next step if the step has changed in the store
   */
  useEffect(() => {
    if (quiz.step === QuizStep.MODEL) {
      navigate(quizModelHref)
    }
  }, [navigate, quiz.step, quizModelHref])

  if (isRedirecting) {
    return <RedirectPartialView />
  }

  return (
    <>
      <Navbar />
      <S.QuizMain>
        <QuizHeader
          title={<FormattedMessage id="quiz_customer_page.content.base.title" />}
          intro={<FormattedMessage id="quiz_customer_page.content.base.intro" />}
          nextStep={<FormattedMessage id="quiz_customer_page.content.base.button_text" />}
          progress={0.5}
          step={0}
        />
        <S.Form onSubmit={formik.handleSubmit}>
          <TextField
            name="name"
            label={messages['quiz_customer_page.content.questions.customer_name.label'] as string}
            placeholder={messages['quiz_customer_page.content.questions.customer_name.placeholder'] as string}
            defaultValue={formik.values.name}
            onChange={handleNameChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.errors.name) && Boolean(formik.touched.name) ? formik.errors.name : undefined}
          />

          <TextField
            name="email"
            type="email"
            label={messages['quiz_customer_page.content.questions.customer_email.label'] as string}
            placeholder={messages['quiz_customer_page.content.questions.customer_email.placeholder'] as string}
            defaultValue={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.errors.email) && Boolean(formik.touched.email) ? formik.errors.email : undefined}
          />
          <S.ActionBar>
            <Tippy
              content={<FormattedMessage id="static.form.hint_continue" />}
              placement="bottom"
              disabled={formik.isValid}>
              <div>
                <Button type="submit" disabled={!formik.isValid || formik.isSubmitting || res.isLoading}>
                  {formik.isSubmitting || res.isLoading ? (
                    <Spinner />
                  ) : (
                    <FormattedMessage id="quiz_customer_page.content.base.button_text" />
                  )}
                </Button>
              </div>
            </Tippy>
          </S.ActionBar>
        </S.Form>
      </S.QuizMain>
      <Footer />
    </>
  )
}

export default CustomerDetails
